import { useEffect, useMemo, useState } from "react";
import { Check, Columns3, Menu, Plus, Repeat, Target } from "lucide-react";
import { clsx } from "clsx";
import { buildLanes, todayIso, type Lane } from "../lib/laneCore";
import { daysLeftLabel } from "../lib/goalCore";
import { api, isTauriEnvironment, type AgentJob, type Project, type Task } from "../lib/tauri";
import { useGoalStore } from "../stores/goalStore";
import { useProjectStore } from "../stores/projectStore";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";
import { GlobalSearchButton } from "./GlobalSearch";
import { KpiChips } from "./KpiChips";
import { useKpiStore } from "../stores/kpiStore";

const COLOR_PRESETS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#a855f7"];
const WEEKDAYS = [
  { value: "1", label: "月" },
  { value: "2", label: "火" },
  { value: "3", label: "水" },
  { value: "4", label: "木" },
  { value: "5", label: "金" },
  { value: "6", label: "土" },
  { value: "7", label: "日" },
];
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => {
  const day = String(i + 1);
  return { value: day, label: i === 30 ? "月末" : `${day}日` };
});
const DOMAIN_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "work", label: "仕事" },
  { value: "life", label: "生活" },
] as const;
const SECTION_DEFS = [
  { key: "decision", label: "要判断" },
  { key: "today", label: "今日" },
  { key: "active", label: "進行中" },
] as const;

export function LaneBoard({ onMenuClick }: { onMenuClick?: () => void }) {
  const allTasks = useTaskStore((s) => s.allTasks);
  const projects = useProjectStore((s) => s.projects);
  const loadProjects = useProjectStore((s) => s.load);
  const domain = useUiStore((s) => s.domain);
  const setDomain = useUiStore((s) => s.setDomain);
  const [jobs, setJobs] = useState<AgentJob[]>([]);
  const [showProjectForm, setShowProjectForm] = useState(false);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const loadGoals = useGoalStore((s) => s.load);
  useEffect(() => {
    void loadGoals();
  }, [loadGoals]);

  // 要判断セクション用（PWA 等の未対応環境は空配列にフォールバック）
  useEffect(() => {
    void api
      .listAgentJobs?.(50)
      .then((j) => setJobs(j ?? []))
      .catch(() => setJobs([]));
  }, [allTasks]);

  const loadKpi = useKpiStore((s) => s.load);

  // 今日の数字をロード + 外部（HTTP/MCP）書き込みの即時反映
  useEffect(() => {
    void loadKpi();
    if (!isTauriEnvironment()) return;
    let unlisten: (() => void) | undefined;
    let disposed = false;
    void import("@tauri-apps/api/event").then(({ listen }) =>
      listen("flow://kpi-changed", () => {
        void loadKpi();
      }).then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
    );
    return () => {
      disposed = true;
      unlisten?.();
    };
  }, [loadKpi]);

  const lanes = useMemo(
    () => buildLanes(allTasks, projects, jobs, domain, todayIso()),
    [allTasks, projects, jobs, domain]
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 dark:border-border-dark">
        <button
          type="button"
          aria-label="メニュー"
          onClick={onMenuClick}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted hover:bg-hover dark:text-muted-dark dark:hover:bg-hover-dark md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h2 className="flex items-center gap-2 text-base font-bold">
          <Columns3 className="h-4 w-4" /> 案件レーン
        </h2>
        <div className="flex items-center gap-1 rounded-md border border-border bg-bg p-0.5 dark:border-border-dark dark:bg-bg-dark">
          {DOMAIN_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setDomain(value)}
              aria-pressed={domain === value}
              className={clsx(
                "rounded px-3 py-1 text-xs font-medium transition",
                domain === value
                  ? "bg-card text-text shadow-sm dark:bg-card-dark dark:text-text-dark"
                  : "text-muted dark:text-muted-dark"
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <GlobalSearchButton className="ml-auto" />
        <button
          type="button"
          onClick={() => setShowProjectForm((v) => !v)}
          className="flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted hover:bg-hover dark:border-border-dark dark:text-muted-dark dark:hover:bg-hover-dark"
        >
          <Plus className="h-3.5 w-3.5" /> プロジェクト
        </button>
      </header>
      {showProjectForm && <ProjectCreateForm onDone={() => setShowProjectForm(false)} />}
      {/* モバイルは横スクロール + snap（設計書: モバイル幅で横スクロール） */}
      <div className="flex min-h-0 flex-1 snap-x snap-mandatory gap-4 overflow-x-auto px-4 py-4">
        {lanes.map((lane) => (
          <LaneColumn key={lane.project?.id ?? "unassigned"} lane={lane} projects={projects} />
        ))}
      </div>
    </div>
  );
}

function ProjectCreateForm({ onDone }: { onDone: () => void }) {
  const createProject = useProjectStore((s) => s.create);
  const showToast = useUiStore((s) => s.showToast);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"work" | "life">("work");
  const [color, setColor] = useState(COLOR_PRESETS[0]);

  const submit = async () => {
    if (!name.trim()) return;
    try {
      await createProject({ name: name.trim(), kind, color });
      onDone();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "プロジェクト作成に失敗しました");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 dark:border-border-dark">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="プロジェクト名"
        className="h-9 w-48 rounded-md border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-bg-dark"
      />
      <div className="flex items-center gap-1 rounded-md border border-border p-0.5 dark:border-border-dark">
        {(["work", "life"] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            className={clsx(
              "rounded px-2 py-1 text-xs",
              kind === k
                ? "bg-brand-500/15 font-semibold text-brand-700 dark:text-brand-200"
                : "text-muted dark:text-muted-dark"
            )}
          >
            {k === "work" ? "仕事" : "生活"}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1">
        {COLOR_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={`色 ${c}`}
            onClick={() => setColor(c)}
            className={clsx(
              "h-6 w-6 rounded-full border-2",
              color === c ? "border-text dark:border-text-dark" : "border-transparent"
            )}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => void submit()}
        disabled={!name.trim()}
        className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
      >
        作成
      </button>
    </div>
  );
}

function LaneColumn({ lane, projects }: { lane: Lane<Task, Project>; projects: Project[] }) {
  const project = lane.project;
  return (
    <section className="flex h-full w-[280px] shrink-0 snap-start flex-col rounded-lg border border-border bg-card dark:border-border-dark dark:bg-card-dark">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5 dark:border-border-dark">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: project?.color ?? "#9ca3af" }}
        />
        <span className="flex-1 truncate text-sm font-semibold">{project?.name ?? "未分類"}</span>
        {project && (
          <span className="rounded-full bg-bg px-2 py-0.5 text-[10px] text-muted dark:bg-bg-dark dark:text-muted-dark">
            {project.kind === "life" ? "生活" : "仕事"}
          </span>
        )}
        <span className="font-mono text-xs text-muted dark:text-muted-dark">{lane.total}</span>
      </div>
      {project && <KpiChips projectId={project.id} />}
      <div className="flow-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {SECTION_DEFS.map(({ key, label }) => {
          const tasks = lane.sections[key];
          if (tasks.length === 0) return null;
          return (
            <div key={key}>
              <div className="mb-1.5 text-[10px] font-semibold text-dim">
                {label} ({tasks.length})
              </div>
              <div className="space-y-1.5">
                {tasks.map((task) => (
                  <TaskRow key={task.id} task={task} projects={projects} />
                ))}
              </div>
            </div>
          );
        })}
        {lane.total === 0 && <p className="py-6 text-center text-xs text-dim">タスクなし</p>}
      </div>
      {project?.kind === "life" && <HabitQuickAdd projectId={project.id} />}
    </section>
  );
}

function TaskRow({ task, projects }: { task: Task; projects: Project[] }) {
  const toggleTask = useTaskStore((s) => s.toggleTask);
  const updateTask = useTaskStore((s) => s.updateTask);
  const setEditorOpen = useUiStore((s) => s.setEditorOpen);
  const setAppView = useUiStore((s) => s.setAppView);
  const goals = useGoalStore((s) => s.goals);
  const setGoalFocus = useGoalStore((s) => s.setFocus);

  const assign = (projectId: string) => {
    void updateTask(task.id, projectId ? { projectId } : { clearProject: true });
  };

  // 部品4: あと◯日チップ（ローカル今日基準。超過は赤）
  const due = daysLeftLabel(task.dueDate, todayIso());
  // ゴールパンくずチップ（タップで枝へジャンプ）
  const goal = task.goalId ? goals.find((g) => g.id === task.goalId) ?? null : null;

  return (
    <div className="rounded-md border border-border bg-bg p-2 dark:border-border-dark dark:bg-bg-dark">
      <div className="flex items-start gap-2">
        <button
          type="button"
          aria-label="完了にする"
          onClick={() => void toggleTask(task.id)}
          className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border text-transparent hover:border-brand-500 hover:text-brand-500 dark:border-border-dark"
        >
          <Check className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => setEditorOpen(true, task.id)}
          className="flex-1 text-left text-sm leading-snug"
        >
          {task.title}
        </button>
        {task.repeatRule && (
          <Repeat aria-label="習慣" className="h-3.5 w-3.5 shrink-0 text-muted dark:text-muted-dark" />
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        {due && (
          <span
            className={clsx(
              "text-[10px]",
              due.overdue ? "font-semibold text-danger-500" : "text-muted dark:text-muted-dark"
            )}
          >
            {due.text}
          </span>
        )}
        {task.priority === "high" && (
          <span className="text-[10px] font-semibold text-danger-500">高</span>
        )}
        {goal && (
          <button
            type="button"
            aria-label={`ゴール ${goal.title} の枝へ`}
            onClick={() => {
              setGoalFocus(goal.id);
              setAppView("goals");
            }}
            className="flex max-w-[110px] items-center gap-0.5 rounded-full bg-brand-500/10 px-1.5 py-0.5 text-[10px] text-brand-700 dark:text-brand-200"
          >
            <Target className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{goal.title}</span>
          </button>
        )}
        <select
          aria-label="プロジェクト割当"
          value={task.projectId ?? ""}
          onChange={(e) => assign(e.target.value)}
          className="ml-auto max-w-[120px] rounded border border-border bg-bg px-1 py-0.5 text-[10px] text-muted dark:border-border-dark dark:bg-bg-dark dark:text-muted-dark"
        >
          <option value="">未分類</option>
          {projects
            .filter((p) => !p.archived)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
}

function HabitQuickAdd({ projectId }: { projectId: string }) {
  const createTask = useTaskStore((s) => s.createTask);
  const showToast = useUiStore((s) => s.showToast);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [freq, setFreq] = useState<"daily" | "weekly" | "monthly">("daily");
  const [weekday, setWeekday] = useState("1");
  const [monthDay, setMonthDay] = useState("31");

  const submit = async () => {
    if (!title.trim()) return;
    const repeatRule = freq === "daily" ? "daily" : freq === "weekly" ? `weekly:${weekday}` : `monthly:${monthDay}`;
    try {
      await createTask({ title: title.trim(), priority: "medium", projectId, repeatRule });
      setTitle("");
      setOpen(false);
      showToast("習慣を追加しました（完了すると次回分が自動で立ちます）");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "習慣の追加に失敗しました");
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center justify-center gap-1 border-t border-border py-2 text-xs font-medium text-muted hover:bg-hover dark:border-border-dark dark:text-muted-dark dark:hover:bg-hover-dark"
      >
        <Repeat className="h-3.5 w-3.5" /> 習慣を追加
      </button>
    );
  }
  return (
    <div className="space-y-2 border-t border-border p-2.5 dark:border-border-dark">
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="習慣名（例: 筋トレ）"
        className="h-8 w-full rounded-md border border-border bg-bg px-2 text-xs dark:border-border-dark dark:bg-bg-dark"
      />
      <div className="flex items-center gap-2">
        <select
          aria-label="頻度"
          value={freq}
          onChange={(e) => setFreq(e.target.value as "daily" | "weekly" | "monthly")}
          className="rounded border border-border bg-bg px-1 py-1 text-xs dark:border-border-dark dark:bg-bg-dark"
        >
          <option value="daily">毎日</option>
          <option value="weekly">毎週</option>
          <option value="monthly">毎月</option>
        </select>
        {freq === "weekly" && (
          <select
            aria-label="曜日"
            value={weekday}
            onChange={(e) => setWeekday(e.target.value)}
            className="rounded border border-border bg-bg px-1 py-1 text-xs dark:border-border-dark dark:bg-bg-dark"
          >
            {WEEKDAYS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}曜
              </option>
            ))}
          </select>
        )}
        {freq === "monthly" && (
          <select
            aria-label="日付"
            value={monthDay}
            onChange={(e) => setMonthDay(e.target.value)}
            className="rounded border border-border bg-bg px-1 py-1 text-xs dark:border-border-dark dark:bg-bg-dark"
          >
            {MONTH_DAYS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!title.trim()}
          className="ml-auto rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"
        >
          追加
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-muted dark:text-muted-dark"
        >
          閉じる
        </button>
      </div>
    </div>
  );
}
